<template>
  <div class="d-flex flex-column">
    <PhaseSection
      data-testid="problem-solving-2d"
      data-tourid="problem-solving-define-phase"
      :isEditMode="isEditMode"
      :title="$t('problem_solving.phases.define')"
      :description="$t('problem_solving.define.define_description')"
      :hasNextButton="hasNextButton"
      :hasBackButton="hasBackButton"
      @next="$emit('next')"
      @back="$emit('back')"
    >
      <TranslationLoading slot="right" v-if="isTranslating" />
      <div slot="right" v-else>
        <p class="d-flex justify-end caption blue-grey--text mb-6 mt-n6">
          * {{ $t('common.required_fields') }}
        </p>
        <DefineProblemTool
          v-if="showDefineProblemTool"
          :open="showDefineProblemTool"
          :problem="problem"
          :problemBreakdown="problemBreakdown"
          :isEditMode="isEditMode"
          @updateProblem="summarizeProblem"
          @setProblemBreakdown="setProblemBreakdown"
          @close="showDefineProblemTool = false"
        />
        <div :class="{ 'mb-4': isEditMode, 'mb-6': !isEditMode }">
          <a
            v-if="showProblemDefinitionToolTrigger"
            class="caption d-flex justify-end pt-n2"
            data-tourid="problemSolving-5w2h-tool-button"
            @click.prevent="openProblemTool"
            >{{ $t('problem_solving.problem_tool.5w2h_tool_name') }}</a
          >
          <TextQuestion
            data-private
            tid="problemSolving-textarea-2d"
            v-model="problem"
            rows="4"
            :tourId="'problemSolving-textArea-whatIsTheProblem'"
            :maxLength="$constants.problemSolving.problemMaxLength"
            :error="markRequiredFields && !problem"
            :question="`${$t('problem_solving.define.problem_label')}*`"
            :isEditMode="isEditMode"
            :placeholder="$t('problem_solving.define.problem_placeholder')"
            @focusOut="trackEvent('addProblem')"
          >
          </TextQuestion>

          <MediaContainer
            v-if="isEditMode || problemMedia.length > 0"
            onlyThumbnails
            smallThumbnails
            :class="{ 'mt-n4': isEditMode, 'mt-2': !isEditMode }"
            :images="problemMedia"
            :readOnly="!isEditMode || isSaving"
            @change="handleProblemMediaChange"
          />
        </div>

        <TextQuestion
          data-private
          v-model="containmentPlan"
          tid="problemSolving-textarea-3dDevelop"
          tourId="problemAnalysis-textField-containmentPlan"
          :maxLength="$constants.problemSolving.containmentPlanMaxLength"
          rows="4"
          :error="markRequiredFields && !containmentPlan"
          :question="containmentLabel"
          :placeholder="
            $t('problem_solving.define.containment_plan_placeholder')
          "
          :isEditMode="isEditMode"
          @focusOut="trackEvent('containmentPlan')"
        />
        <MediaContainer
          v-if="isEditMode || countermeasuresMedia.length > 0"
          onlyThumbnails
          smallThumbnails
          :class="{ 'mt-n4': isEditMode, 'mt-2': !isEditMode }"
          :images="countermeasuresMedia"
          :readOnly="!isEditMode || isSaving"
          @change="handleCountermeasuresMediaChange"
        />

        <div :class="{ 'mt-4': isEditMode, 'mt-4 mb-6': !isEditMode }">
          <TextQuestion
            data-private
            v-model="specificGoals"
            tid="problemSolving-textarea-3dSpecificGoals"
            tourId="problemAnalysis-textField-specificGoals"
            :maxLength="$constants.problemSolving.goalsMaxLength"
            rows="4"
            :question="specificGoalsLabel"
            :error="markRequiredFields && !specificGoals"
            :placeholder="$t('problem_solving.define.goal_placeholder')"
            :isEditMode="isEditMode"
            @focusOut="trackEvent('containmentPlan')"
          />
        </div>

        <div class="mb-6">
          <TextQuestion
            data-private
            v-model="businessRequirements"
            tid="problemSolving-textarea-3dBusinessRequirements"
            tourId="problemAnalysis-textField-businessRequirements"
            :maxLength="$constants.problemSolving.businessRequirementsMaxLength"
            rows="1"
            :question="$t('problem_solving.define.business_requirements_label')"
            :placeholder="
              $t('problem_solving.define.business_requirements_placeholder')
            "
            :isEditMode="isEditMode"
            @focusOut="trackEvent('businessRequirements')"
          />
        </div>

        <div class="mb-6" data-tourid="problemSolving-process-map-phase">
          <p class="grey--text text--darken-2 ma-0 pa-0">
            {{ $t('problem_solving.define.process_map_label') }}
          </p>
          <p class="grey--text text--darken-1 font-size-12">
            {{ $t('problem_solving.define.process_map_description') }}
          </p>
          <MediaContainer
            v-if="isEditMode || processMapMedia.length > 0"
            onlyThumbnails
            smallThumbnails
            :images="processMapMedia"
            :readOnly="!isEditMode || isSaving"
            @change="handleProcessMapMediaChange"
          />
        </div>
        <div class="mt-3">
          <CategoriesSelector
            data-private
            data-testid="problem-solving-problem-analysis-benefits"
            v-if="isEditMode"
            v-model="selectedCategories"
            :siteIds="[siteId]"
          />
          <ChipsList
            v-else
            translationKey="common.categories"
            :items="selectedCategories"
            :title="$t('common.category_other')"
          />
        </div>
        <v-alert
          outlined
          color="blue lighten-1"
          text
          v-if="showGapsCard"
          class="mt-6 py-6 px-8"
        >
          <p
            class="caption text-center grey--text text--darken-4"
            v-html="
              $t(
                'problem_solving.define.gap_analysis_card_descriptionWithCount',
                { count: totalProcessGaps }
              )
            "
          />

          <div class="text-center mt-4">
            <Button
              plain
              color="primary"
              class="blue--text"
              data-tourid="problem-solving-gaps-tool-button"
              icon="mdi-tools"
              :text="
                totalProcessGaps === 0
                  ? $t('problem_solving.define.gap_analysis_trigger_pristine')
                  : $t('problem_solving.define.gap_analysis_trigger')
              "
              @click="openGapsTool"
            />
          </div>
        </v-alert>
        <GapsIdentificationTool
          v-if="showGapsTool"
          :rev="rev"
          :show="showGapsTool"
          :canAddGaps="isEditMode"
          @close="closeGapsTool"
          @track="trackEvent"
        />
      </div>
    </PhaseSection>
  </div>
</template>

<script>
import { mapMutations } from 'vuex'
import trackerMixin from '@/mixins/tracking/trackerMixin.vue'
import TextQuestion from '@/components/Rev/TextQuestion.vue'
import PhaseSection from '@/components/Rev/PhaseSection/PhaseSection.vue'
import CategoriesSelector from '@/components/CategoriesSelector.vue'
import MediaContainer from '@/components/MediaContainer/MediaContainer.vue'
import DefineProblemTool from '@/containers/ProblemSolvingView/DefineProblemTool/DefineProblemTool.vue'
import GapsIdentificationTool from '@/containers/ProblemSolvingView/GapsTools/GapsIdentificationTool/GapsIdentificationTool.vue'
import TranslationLoading from '@/components/Rev/TranslationLoading.vue'
import ChipsList from '@/components/ChipsList.vue'
import Button from '@/components/UI/Button'

export default {
  name: 'ProblemAnalysisStep',

  props: {
    isEditMode: {
      type: Boolean,
      default: false,
    },

    rev: {
      type: Object,
      required: true,
    },

    isSaving: Boolean,
    isTranslating: Boolean,
    hasNextButton: Boolean,
    hasBackButton: Boolean,
    markRequiredFields: Boolean,
  },

  components: {
    TextQuestion,
    PhaseSection,
    CategoriesSelector,
    DefineProblemTool,
    GapsIdentificationTool,
    MediaContainer,
    TranslationLoading,
    ChipsList,
    Button,
  },

  mixins: [trackerMixin],

  data() {
    return {
      showDefineProblemTool: false,
      showGapsTool: false,
    }
  },

  computed: {
    selectedCategories: {
      get() {
        return this.rev?.categories ?? []
      },

      set(categories) {
        this.$emit('revChange')
        this.trackUpdate(this.rev, 'changeCategories')
        return this.updateCategories(categories)
      },
    },
    problem: {
      get() {
        return this.rev?.problemAnalysis?.problem ?? ''
      },
      set(problem) {
        this.summarizeProblem(problem)
      },
    },

    problemMedia() {
      return this.rev?.problemAnalysis?.problemMedia ?? []
    },

    containmentPlan: {
      get() {
        return this.rev?.problemAnalysis?.containment ?? ''
      },
      set(containmentPlan) {
        this.$emit('revChange')
        this.updateContainmentPlan(containmentPlan)
        this.trackEvent('defineAproach')
      },
    },

    specificGoals: {
      get() {
        return this.rev?.problemAnalysis?.goals ?? ''
      },
      set(specificGoals) {
        this.$emit('revChange')
        this.updateSpecificGoals(specificGoals)
        this.trackEvent('defineAproach')
      },
    },

    businessRequirements: {
      get() {
        return this.rev?.problemAnalysis?.businessRequirements ?? ''
      },
      set(businessRequirements) {
        this.$emit('revChange')
        this.updateBusinessRequirements(businessRequirements)
        this.trackEvent('businessRequirements')
      },
    },

    problemBreakdown: {
      get() {
        return this.rev?.problemBreakdown ?? {}
      },
      set(problemBreakdown) {
        this.setProblemBreakdown(problemBreakdown)
      },
    },

    measureDescription: {
      get() {
        return this.rev?.problemAnalysis?.measureDescription ?? ''
      },
      set(measureDescription) {
        this.$emit('revChange')
        this.updateMeasureDescription(measureDescription)
        this.trackEvent('measureDescription')
      },
    },

    showProblemDefinitionToolTrigger() {
      const answers = Object.values(this.problemBreakdown)
      const hasAnswers = answers
        .map(answer => Boolean(answer?.trim()))
        .includes(true)

      return hasAnswers || this.isEditMode
    },

    totalProcessGaps() {
      return this.rev?.processGaps?.length ?? 0
    },

    showGapsCard() {
      return (
        this.totalProcessGaps > 0 ||
        this.isEditMode ||
        this.rev?.processMapMedia?.[0]
      )
    },

    countermeasuresMedia() {
      return this.rev?.problemAnalysis?.countermeasuresMedia ?? []
    },

    siteId() {
      return this.rev?.location?.site?._id || this.rev?.location?.site
    },

    processMapMedia() {
      return this.rev?.processMapMedia ?? []
    },

    containmentLabel() {
      return this.specificGoals
        ? this.$t('problem_solving.define.containment_plan_label')
        : `${this.$t('problem_solving.define.containment_plan_label')}*`
    },

    specificGoalsLabel() {
      return this.containmentPlan
        ? this.$t('problem_solving.define.goal_label')
        : `${this.$t('problem_solving.define.goal_label')}*`
    },
  },

  methods: {
    ...mapMutations({
      updateProblem: 'problemSolving/updateProblem',
      updateProblemMedia: 'problemSolving/updateProblemMedia',
      updateCountermeasuresMedia: 'problemSolving/updateCountermeasuresMedia',
      updateContainmentPlan: 'problemSolving/updateContainmentPlan',
      updateSpecificGoals: 'problemSolving/updateSpecificGoals',
      updateRootCauseMedia: 'problemSolving/updateRootCauseMedia',
      updateRootCauses: 'problemSolving/updateRootCauses',
      updateCategories: 'problemSolving/updateCategories',
      updateBusinessRequirements: 'problemSolving/updateBusinessRequirements',
      updateProblemBreakdown: 'problemSolving/updateProblemBreakdown',
      updateProcessMap: 'problemSolving/updateProcessMapMedia',
    }),

    setProblemBreakdown(problemBreakdown) {
      this.$emit('revChange')
      this.updateProblemBreakdown(problemBreakdown)
    },

    summarizeProblem(problem) {
      this.$emit('revChange')
      this.updateProblem(problem)
    },

    handleProblemMediaChange(media) {
      this.trackEvent('problemDefinitionPhoto')
      this.$emit('revChange')
      this.updateProblemMedia(media)
    },

    handleCountermeasuresMediaChange(media) {
      this.trackEvent('containtmentPhoto')
      this.$emit('revChange')
      this.updateCountermeasuresMedia(media)
    },

    handleProcessMapMediaChange(media) {
      this.trackEvent('processMapPhoto')
      this.$emit('revChange')
      this.updateProcessMap(media)
    },

    trackEvent(changedField) {
      this.trackUpdate(this.rev, changedField)
    },

    openProblemTool() {
      this.showDefineProblemTool = true
      this.trackTool('5W2H', 'launch', 'problem-solving')
    },

    openGapsTool() {
      this.showGapsTool = true
      this.trackTool('GapIdentification', 'launch', 'problem-solving')
    },

    closeGapsTool() {
      this.showGapsTool = false
      this.trackTool('GapIdentification', 'close', 'problem-solving')
    },
  },
}
</script>
